import type { ReactNode } from "react"
import { Head, Link } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Breadcrumb, categoryLabel, type Crumb, OwnBadge, ReviewButton } from "./shared"

type AliasRow = {
  id: number
  alias_string: string
  source_kind: string
  source_kind_label: string
  resolved_by: string
  confidence: number | null
  dq_reason: string | null
}

type MarketLink = { id: number; niq_key: string; resolved_by: string }

type Leaf = {
  id: number
  canonical_name: string
  pack_size: string | null
  variant_name: string | null
  range_name: string | null
  star_product_code: string | null
  niq_key: string | null
  is_own: boolean
  status: string | null
  brand: { id: number; name: string; tracked_category: string | null }
  manufacturer: { id: number; name: string }
}

type Props = { leaf: Leaf; aliases: AliasRow[]; market_links: MarketLink[]; breadcrumb: Crumb[] }

function ResolvedBadge({ by }: { by: string }) {
  return by === "human" ? <Badge tone="signal">Manual</Badge> : <Badge tone="muted">Otomatis</Badge>
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink-body">{children}</dd>
    </div>
  )
}

export default function Show({ leaf, aliases, market_links, breadcrumb }: Props) {
  const path = "/admin/product-master"
  const description = `Identitas kanonik untuk ${leaf.canonical_name} — alias dari setiap dataset dan tautan pasar NIQ.`

  return (
    <>
      <Head title={`${leaf.canonical_name} — Produk Master`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${leaf.canonical_name} — Produk Master`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell width="wide">
        <Breadcrumb crumbs={breadcrumb} />
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <h1>{leaf.canonical_name}</h1>
              <OwnBadge isOwn={leaf.is_own} />
            </div>
            <ReviewButton />
          </div>
        </div>

        {/* Canonical identity */}
        <section className="mt-6">
          <h2 className="text-sm font-medium text-ink-display">Identitas kanonik</h2>
          <dl className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-hairline bg-surface p-5 sm:grid-cols-3">
            <Field label="Manufaktur">
              <Link href={`${path}?manufacturer_id=${leaf.manufacturer.id}`} className="font-medium">
                {leaf.manufacturer.name}
              </Link>
            </Field>
            <Field label="Merek">
              <Link href={`${path}?brand_id=${leaf.brand.id}`} className="font-medium">
                {leaf.brand.name}
              </Link>
            </Field>
            <Field label="Kategori">{categoryLabel(leaf.brand.tracked_category) ?? "—"}</Field>
            <Field label="Ukuran (pack)">{leaf.pack_size ?? "—"}</Field>
            <Field label="Varian">{leaf.variant_name ?? "—"}</Field>
            <Field label="Range">{leaf.range_name ?? "—"}</Field>
            <Field label="Kode STAR">
              {leaf.star_product_code ? <span className="font-mono text-xs">{leaf.star_product_code}</span> : "—"}
            </Field>
            <Field label="NIQ key">
              {leaf.niq_key ? <span className="font-mono text-xs">{leaf.niq_key}</span> : "—"}
            </Field>
            <Field label="Status">{leaf.status ?? "—"}</Field>
          </dl>
        </section>

        {/* Aliases */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Alias ({aliases.length})</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Label produk mentah dari setiap dataset yang ter-crosswalk ke leaf ini.
          </p>
          {aliases.length === 0 ? (
            <div className="mt-3 rounded-lg border border-hairline bg-surface p-6 text-center text-sm text-ink-muted">
              Belum ada alias yang tertaut ke produk ini.
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Label</th>
                    <th className="px-4 py-3 font-medium">Sumber</th>
                    <th className="px-4 py-3 font-medium">Resolusi</th>
                    <th className="px-4 py-3 font-medium">Keyakinan</th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map((a) => (
                    <tr key={a.id} className="border-t border-hairline">
                      <td className="px-4 py-3 text-ink-body">{a.alias_string}</td>
                      <td className="px-4 py-3">
                        <Badge tone="neutral">{a.source_kind_label}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <ResolvedBadge by={a.resolved_by} />
                      </td>
                      <td className="px-4 py-3 text-ink-muted">
                        {a.confidence != null ? a.confidence.toFixed(2) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Market (NIQ) links */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Tautan pasar NIQ ({market_links.length})</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Node pasar NIQ yang dijembatani ke leaf ini (own↔NIQ, bisa 1-ke-banyak).
          </p>
          {market_links.length === 0 ? (
            <div className="mt-3 rounded-lg border border-hairline bg-surface p-6 text-center text-sm text-ink-muted">
              {leaf.is_own
                ? "Produk own ini belum tertaut ke node pasar NIQ."
                : "Leaf kompetitor — identitasnya adalah NIQ key di atas."}
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">NIQ key</th>
                    <th className="px-4 py-3 font-medium">Resolusi</th>
                  </tr>
                </thead>
                <tbody>
                  {market_links.map((l) => (
                    <tr key={l.id} className="border-t border-hairline">
                      <td className="px-4 py-3 font-mono text-xs text-ink-body">{l.niq_key}</td>
                      <td className="px-4 py-3">
                        <ResolvedBadge by={l.resolved_by} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3">
            <Link href="/admin/product-market-links" className="text-sm">
              Tinjau semua tautan pasar →
            </Link>
          </div>
        </section>
      </AppShell>
    </>
  )
}
